import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { Observable, Subscription } from 'rxjs';

import { TCenter } from '@shared/interfaces/TCenter';
import { TProject } from '@shared/interfaces/TProject';
import { TUser, UserAccessLevel, UserLevel } from '@shared/interfaces/TUser';

import { Router } from '@angular/router';
import { ButtonsState } from 'src/app/interfaces/buttons-state.interface';
import { Region } from 'src/app/interfaces/region.interface';
import { YearMonth } from 'src/app/interfaces/year-month.interface';
import { AuthService } from 'src/app/services/auth.service';
import { CategoriesService } from 'src/app/services/categories.service';
import { TRegion } from 'src/app/services/regions.service';
import { KamReportsService } from './services/kam-reports.service';


@Component({
  selector: 'app-kam-reports',
  templateUrl: './kam-reports.component.html',
  styleUrls: ['./kam-reports.component.scss']
})
export class KamReportsComponent implements OnInit, OnDestroy {

  @Input() projectId: string;

  private user: TUser;
  public project: TProject;
  public regions: Region[] = [];
  private _subscriptions: Subscription[] = [];

  // regions selector
  public regionsFormControl: FormControl = new FormControl('');

  // countries selector
  public countries: TRegion[] = [];
  public selectedCountriesFC = new FormControl<TRegion[]>([]);
  public selectedCountries: string[] = [];
  public countriesFiltered: TRegion[] = [];
  public selectedCountriesSummary: string = '';

  public closestSalesDate: YearMonth | null;

  // report buttons state
  public reportButtonsState: ButtonsState = {
    territoryReview: false,
    salesLeaderReview: false,
    marketingReview:  false,
  };
  public selectedReport: string;
  public generateReportButtonState: boolean = false;
  public UserAccessLevel = UserAccessLevel;

  public Number = Number; // for template usage
  public location = window.location; // for template usage

  constructor(
    private _authService: AuthService,
    private _kamReportsService: KamReportsService,
    private _categoriesService: CategoriesService,
    private _router: Router,
  ) {}

  get centers(): TCenter[] { return this._kamReportsService.allCenters; }
  get selectedRegions(): Region[] { return this._kamReportsService.selectedRegions; }
  get centers$(): Observable<TCenter[]> { return this._kamReportsService.allCenters$; }
  get selectedRegions$(): Observable<Region[]> { return this._kamReportsService.selectedRegions$; }

  public ngOnInit(): void {
    this.user = this._authService.getCurrentUser() as TUser;
    const project: TProject | undefined = this._categoriesService.getProject(this.projectId);
    if (project) {
      this.project = project;
      this.closestSalesDate = this._kamReportsService.getClosestSalesDate(this.project?.accountBricks);
      this.regions = this._kamReportsService.getRegions(this.user, this.project);
    } else {
      // this._router.navigate([`/${ROUTES_PATHS.root}`]);
    }
    this._subscriptions.push(
      // countries selector
      this.selectedCountriesFC.valueChanges.subscribe(val => {
        if (val) {
          this._kamReportsService.selectedCountries = [...val.map(val => val.value)];
          this.changeGenerateReportButtonState(true);
        }
      })
    );
  }

  public ngOnDestroy() {
    this._subscriptions.forEach((item) => item.unsubscribe());
  }

  /**
   * Set selected regions
   * @param  {MatSelectChange} event
   * @returns void
   */
  setSelectedRegions(event: MatSelectChange): void {
    const newRegions: Region[] = [];
    if (Array.isArray(event.value)) {
      event.value.forEach((regionId: string) => {
        this.regions.forEach(region => {
          if (region.id === regionId) {
            newRegions.push(region);
          }
        });
      });
    } else {
      this.regions.forEach(region => {
        if (region.id === event.value) {
          newRegions.push(region);
        }
      });
    }
    this._kamReportsService.selectedRegions = newRegions;
    this.changeGenerateReportButtonState(true);
  }


  selectTerritoryReview(): void {
    this.changeReportButtonsState('territoryReview');
    this.changeGenerateReportButtonState(true);
  }

  selectSalesLeaderReview(): void {
    this.changeReportButtonsState('salesLeaderReview');
    this.changeGenerateReportButtonState(true);
    this._kamReportsService.selectedRegions = [...this.regions];
    // TODO select all regions in form control
  }

  selectMarketingReview(): void {
    this.changeReportButtonsState('marketingReview');
    this.changeGenerateReportButtonState(true);
  }

  // report buttons state control
  changeReportButtonsState(selected: string): void {
    Object.keys(this.reportButtonsState).map(key => {
      if (key === selected) { this.reportButtonsState[key] = true }
      else { this.reportButtonsState[key] = false }
    });
    this.reportButtonsState = { ...this.reportButtonsState }
    this.selectedReport = Object.keys(this.reportButtonsState).filter(key => !!this.reportButtonsState[key])[0];
  }

  changeGenerateReportButtonState(val: boolean): void {
    if (!!val && !!this.selectedReport) {
      this.generateReportButtonState = true;
    } else {
      this.generateReportButtonState = false;
    }
  }

  getProjectCenters$(projectId: string, selectedCountries: string[] = [], selectedRegions: string[] = []): Observable<TCenter[]> {
    return this._kamReportsService.getAllProjectCenters$(projectId, selectedCountries, selectedRegions);
  }

  public generateReport(): void {
    this._subscriptions.push(
      this.getProjectCenters$(this.projectId, this._kamReportsService.selectedCountries, this._kamReportsService.selectedRegions.map(region => region.id)).subscribe((centers: TCenter[]) => {
        this._kamReportsService.allCenters = centers;
        this.changeGenerateReportButtonState(false);
      }),
    );
  }

  isButtonDisabled(level: UserAccessLevel[]): boolean {
    if (this.user?.level === UserLevel.Admin) return false;
    if (level.includes(this.user?.userAccessLevel)) return false
    return true;
  }

  getPharmacyDataUrl(): string {
    if (this.project?.regionFiles) {
      const regionFiles = this.project.regionFiles.find(files => files.regionId === this._kamReportsService.selectedRegions[0].id);
      return regionFiles?.pharmacyDataUrl || '';
    }
    return '';
  }

  getBrickDataUrl(): string {
    if (this.project?.regionFiles) {
      const regionFiles = this.project.regionFiles.find(files => files.regionId === this._kamReportsService.selectedRegions[0].id);
      return regionFiles?.brickDataUrl || '';
    }
    return '';
  }
}

